'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/contexts/ToastContext';

interface ImpersonatedUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  orgRole?: string;
  teamIds?: string[];
  projectIds?: string[];
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  realUser: User | null;
  canImpersonate: boolean;
  startImpersonation: (userId: string, orgId: string) => Promise<void>;
  stopImpersonation: () => void;
  getEffectiveUser: () => User | ImpersonatedUser | null;
  getEffectiveUserId: () => string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const toast = useToast();
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [canImpersonate, setCanImpersonate] = useState(false);

  // Check if user can impersonate (any super admin)
  useEffect(() => {
    const checkImpersonationPermission = async () => {
      if (!user) {
        setCanImpersonate(false);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();
        const isSuperAdmin = idTokenResult.claims.superAdmin === true;

        // Super admins can always impersonate
        setCanImpersonate(isSuperAdmin);
      } catch (error) {
        console.error('Error checking impersonation permission:', error);
        setCanImpersonate(false);
      }
    };

    checkImpersonationPermission();
  }, [user]);

  const startImpersonation = async (userId: string, orgId: string) => {
    if (!canImpersonate) {
      toast.error('You do not have permission to impersonate users');
      return;
    }

    try {
      // Get user data
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        toast.error('User not found');
        return;
      }

      const userData = userDoc.data();

      // Get user's org membership
      const membershipRef = doc(db, 'orgs', orgId, 'memberships', userId);
      const membershipDoc = await getDoc(membershipRef);

      let orgRole = 'member';
      if (membershipDoc.exists()) {
        orgRole = membershipDoc.data().role;
      }

      // Create impersonated user object
      const impersonated: ImpersonatedUser = {
        uid: userId,
        email: userData.email || null,
        displayName: userData.displayName || null,
        photoURL: userData.photoURL || userData.cachedPhotoURL || null,
        orgRole
      };

      setImpersonatedUser(impersonated);

      // Store in session storage for persistence across page refreshes
      sessionStorage.setItem('impersonation', JSON.stringify({
        user: impersonated,
        orgId,
        startTime: Date.now()
      }));

      toast.success(`Now impersonating ${userData.displayName || userData.email || 'user'}`);
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error('Failed to start impersonation');
    }
  };

  const stopImpersonation = () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem('impersonation');
    toast.success('Stopped impersonation');
  };

  // Restore impersonation from session storage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('impersonation');
    if (stored && canImpersonate) {
      try {
        const { user: storedUser, startTime } = JSON.parse(stored);

        // Only restore if less than 1 hour old
        if (Date.now() - startTime < 3600000) {
          setImpersonatedUser(storedUser);
        } else {
          sessionStorage.removeItem('impersonation');
        }
      } catch (error) {
        console.error('Error restoring impersonation:', error);
        sessionStorage.removeItem('impersonation');
      }
    }
  }, [canImpersonate]);

  // Clear impersonation if debug mode expires
  useEffect(() => {
    if (!canImpersonate && impersonatedUser) {
      stopImpersonation();
    }
  }, [canImpersonate, impersonatedUser]);

  const getEffectiveUser = () => {
    return impersonatedUser || user;
  };

  const getEffectiveUserId = () => {
    return impersonatedUser?.uid || user?.uid || null;
  };

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating: !!impersonatedUser,
        impersonatedUser,
        realUser: user,
        canImpersonate,
        startImpersonation,
        stopImpersonation,
        getEffectiveUser,
        getEffectiveUserId
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}